import animateScrollTo from 'animated-scroll-to';
import * as Immutable from 'immutable';
import * as t from 'io-ts';
import moment from 'moment';
import { Action, createActions } from 'redux-actions';
import mapValues from 'lodash/mapValues';

import { ENDPOINT_VERSION, ENDPOINTS_ALPHAS, ENDPOINTS_SIMULATIONS } from '../../config';
import { localeData } from '../../locales/data';
import {
  AlphasDataResults,
  AlphasListingData,
  CorrelationProd,
  CorrelationSelf,
  CorrelationSelfType,
} from '../../reducers/AlphasReducer/AlphasReducer.types';
import { CheckSubmissionValidation } from '../../reducers/SimulationReducer/DataReducer/DataReducer.types';
import { FAQ_URL } from '../../shared/constants';
import { concatStringInArrayOfOBjects, prefixEditTabMsg } from '../../shared/helpers';
import {
  globalFetch,
  ioTsValidationError,
  isAction,
  specialCasesCorrelation,
  webSimApiErrorFlashMessages,
} from '../../shared/helpers/fetch-helper';
import { logErrorService } from '../../shared/services';
import { sessionExpire } from '../AuthenticationActions';
import { setTabCodeError } from '../EditorActions/EditorActions';
import {
  addFlashMessages,
  removeFlashContexts,
  removeFlashMessagesContexts,
  removeFlashMessagesContextsErrors,
} from '../FlashMessagesActions';
import { invalidRequest, retryAfterRequest } from '../GenericActions';
import { getPerformanceEndpoint, getScoringCompetition } from '../actionHelpers';
import { resetProperties, setAlphaPropertiesData } from '../AlphasPropertiesAction';

export const {
  responseDataSimulateSuccess,
  responseSimulationCheck,
  requestSimulationCheck,
  responseSimulationCheckFailure,
  requestDataSimulate,
  responseDataSimulateFailure,
  requestRecordsSetSimulation,
  responseRecordsSetSimulationFailure,
  responseRecordsSetSimulationSuccess,
  requestRecordsSetChartSimulation,
  responseRecordsSetChartSimulationFailure,
  responseRecordsSetChartSimulationSuccess,
  requestRecordsSetSummarySimulation,
  responseRecordsSetSummarySimulationFailure,
  responseRecordsSetSummarySimulationSuccess,
  requestSubmissionSimulation,
  responseSubmissionSimulationFailure,
  responseSubmissionSimulationSuccess,
  changeTypeOfChart,
  updateActiveButtonChartSimulation,
  updateHideButtonChartSimulation,
  saveSimulationProperties,
  requestAlphasFromAlphaSimulate,
  responseAlphasFromAlphaSimulateFailure,
  responseAlphasFromAlphaSimulateSuccess,
  changeSimulationStatus,
} = createActions({
  RESPONSE_DATA_SIMULATE_SUCCESS: (idParent: string, childId: string, alpha: any) => ({ idParent, childId, alpha }),
  RESPONSE_SIMULATION_CHECK: (idParent: string, childId: string, alpha: any) => ({ idParent, childId, alpha }),
  REQUEST_SIMULATION_CHECK: (idParent: string, childId: string) => ({ idParent, childId }),
  RESPONSE_SIMULATION_CHECK_FAILURE: (idParent: string, childId: string) => ({ idParent, childId }),
  REQUEST_DATA_SIMULATE: (idParent: string, childId: string) => ({ idParent, childId }),
  RESPONSE_DATA_SIMULATE_FAILURE: (idParent: string, childId: string) => ({ idParent, childId }),
  REQUEST_RECORDS_SET_SIMULATION: (idParent: string, childId: any) => ({ idParent, childId }),
  RESPONSE_RECORDS_SET_SIMULATION_FAILURE: (idParent: string, childId: string) => ({ idParent, childId }),
  RESPONSE_RECORDS_SET_SIMULATION_SUCCESS: (idParent: string, childId: string, data: any) => ({ idParent, childId, data }),
  REQUEST_RECORDS_SET_CHART_SIMULATION: (idParent: string, childId: string) => ({ idParent, childId }),
  RESPONSE_RECORDS_SET_CHART_SIMULATION_FAILURE: (idParent: string, childId: string) => ({ idParent, childId }),
  RESPONSE_RECORDS_SET_CHART_SIMULATION_SUCCESS: (idParent: string, childId: string, data: any) => ({ idParent, childId, data }),
  REQUEST_RECORDS_SET_SUMMARY_SIMULATION: (idParent: string, childId: string) => ({ idParent, childId }),
  RESPONSE_RECORDS_SET_SUMMARY_SIMULATION_FAILURE: (idParent: string, childId: string) => ({ idParent, childId }),
  RESPONSE_RECORDS_SET_SUMMARY_SIMULATION_SUCCESS: (idParent: string, childId: string, data: any) => ({ idParent, childId, data }),
  REQUEST_SUBMISSION_SIMULATION: (idParent: string, childId: string) => ({ idParent, childId }),
  RESPONSE_SUBMISSION_SIMULATION_FAILURE: (idParent: string, childId: string) => ({ idParent, childId }),
  RESPONSE_SUBMISSION_SIMULATION_SUCCESS: (idParent: string, childId: string) => ({ idParent, childId }),
  CHANGE_TYPE_OF_CHART: (idParent: string, childId: string, chartType: string) => ({ idParent, childId, chartType }),
  UPDATE_ACTIVE_BUTTON_CHART_SIMULATION: (idParent: string, childId: string, data: string) => ({ idParent, childId, data }),
  UPDATE_HIDE_BUTTON_CHART_SIMULATION: (idParent: string, childId: string, data: string) => ({ idParent, childId, data }),
  SAVE_SIMULATION_PROPERTIES: (idParent: string, childId: string, data: string) => ({ idParent, childId, data }),
  REQUEST_ALPHAS_FROM_ALPHA_SIMULATE: (idParent: string) => ({ idParent }),
  RESPONSE_ALPHAS_FROM_ALPHA_SIMULATE_FAILURE: (idParent: string) => ({ idParent }),
  RESPONSE_ALPHAS_FROM_ALPHA_SIMULATE_SUCCESS: (idParent: string, alpha: any) => ({ idParent, alpha }),
  CHANGE_SIMULATION_STATUS: (tabIndex: number, currentStatus: string, indexChild: number, nextStatus: string) => ({
    tabIndex,
    indexChild,
    currentStatus,
    nextStatus,
  }),
});

export const {
  requestSimulationCorrelation,
  requestSimulationCorrelationPowerPool,
  responseSimulationCorrelationSuccess,
  responseSimulationCorrelationSuccessPowerPool,
  responseSimulationCorrelationFailure,
  responseSimulationCorrelationFailurePowerPool,
  requestSimulationCorrelationProd,
  responseSimulationCorrelationProdSuccess,
  responseSimulationCorrelationProdFailure,
  requestUpdateAlphaPropertiesSimulation,
  responseUpdateAlphaPropertiesSimulationFailure,
  responseUpdateAlphaPropertiesSimulation,
  requestSimulationAlphasPerformanceComparison,
  responseSimulationAlphasPerformanceComparisonFailure,
  responseSimulationAlphasPerformanceComparisonSuccess,
  requestSelectionCode,
  responseSelectionCodeFailure,
  responseSelectionCodeSuccess,
} = createActions({
  REQUEST_RECORDS_SET_SUMMARY_SIMULATION: (idParent: string, childId: string) => ({ idParent, childId }),
  RESPONSE_RECORDS_SET_SUMMARY_SIMULATION_SUCCESS: (data: any, idParent: string, childId: string) => ({ idParent, childId, data }),
  RESPONSE_RECORDS_SET_SUMMARY_SIMULATION_FAILURE: (idParent: string, childId: string, data: any) => ({ idParent, childId, data }),
  REQUEST_SIMULATION_CORRELATION: (idParent: string, childId: string) => ({ idParent, childId }),
  REQUEST_SIMULATION_CORRELATION_POWER_POOL: (idParent: string, childId: string) => ({ idParent, childId }),
  RESPONSE_SIMULATION_CORRELATION_SUCCESS: (idParent: string, childId: string, data: CorrelationSelfType) => ({ idParent, childId, data }),
  RESPONSE_SIMULATION_CORRELATION_SUCCESS_POWER_POOL: (idParent: string, childId: string, data: CorrelationSelfType) => ({
    idParent,
    childId,
    data,
  }),
  RESPONSE_SIMULATION_CORRELATION_FAILURE: (idParent: string, childId: string, data: any, statusCode?: number) => ({
    idParent,
    childId,
    data,
    statusCode,
  }),
  RESPONSE_SIMULATION_CORRELATION_FAILURE_POWER_POOL: (idParent: string, childId: string, data: any, statusCode?: number) => ({
    idParent,
    childId,
    data,
    statusCode,
  }),
  REQUEST_SIMULATION_CORRELATION_PROD: (idParent: string, childId: string) => ({ idParent, childId }),
  RESPONSE_SIMULATION_CORRELATION_PROD_SUCCESS: (idParent: string, childId: string, data: any) => ({ idParent, childId, data }),
  RESPONSE_SIMULATION_CORRELATION_PROD_FAILURE: (idParent: string, childId: string, data: any, statusCode?: number) => ({
    idParent,
    childId,
    data,
    statusCode,
  }),
  REQUEST_UPDATE_ALPHA_PROPERTIES_SIMULATION: (idParent: string, childId: string) => ({ idParent, childId }),
  RESPONSE_UPDATE_ALPHA_PROPERTIES_SIMULATION_FAILURE: (idParent: string, childId: string) => ({ idParent, childId }),
  RESPONSE_UPDATE_ALPHA_PROPERTIES_SIMULATION: (idParent: string, childId: string, data: any) => ({ idParent, childId, data }),
  REQUEST_SIMULATION_ALPHAS_PERFORMANCE_COMPARISON: (idParent: string, childId: string) => ({ idParent, childId }),
  RESPONSE_SIMULATION_ALPHAS_PERFORMANCE_COMPARISON_FAILURE: (idParent: string, childId: string) => ({ idParent, childId }),
  RESPONSE_SIMULATION_ALPHAS_PERFORMANCE_COMPARISON_SUCCESS: (idParent: string, childId: string, data: any, date: any) => ({
    idParent,
    childId,
    data,
    date,
  }),
  REQUEST_SELECTION_CODE: (
    idParent: string,
    selection: string,
    delay: string,
    region: string,
    instrumentType: string,
    selectionLimit: string,
    selectionHandling: string
  ) => ({ idParent, selection, delay, region, instrumentType, selectionLimit, selectionHandling }),
  RESPONSE_SELECTION_CODE_FAILURE: (idParent: string) => ({ idParent }),
  RESPONSE_SELECTION_CODE_SUCCESS: (idParent: string, data) => ({ idParent, data }),
});

export const getRecordsSetChartSimulation =
  (alphaId: string, record: string, idParent: string, childId: string | null) =>
  async (dispatch: any): Promise<object> => {
    if (alphaId === undefined || record === undefined) {
      return dispatch(invalidRequest());
    }

    dispatch(requestRecordsSetChartSimulation(idParent, childId));

    return fetch(`${ENDPOINTS_ALPHAS.alpha}/${alphaId}/recordsets/${record}`, {
      method: 'GET',
      credentials: 'include',
      headers: new Headers({ Accept: `application/json;${ENDPOINT_VERSION}` }),
    })
      .then(async (response: any): Promise<object> => {
        if (response.ok) {
          const timeOut = response.headers.get('Retry-After');
          if (timeOut !== null) {
            setTimeout(() => dispatch(getRecordsSetChartSimulation(alphaId, record, idParent, childId)), timeOut * 1000);

            return dispatch(retryAfterRequest());
          } else {
            return response.json();
          }
        } else if (response.status === 401) {
          logErrorService(response.statusText, response.status);

          await dispatch(sessionExpire());

          return dispatch(responseRecordsSetChartSimulationFailure(idParent, childId));
        } else {
          let tabID = childId ? childId : idParent;
          tabID = tabID ? tabID : idParent;

          return response
            .json()
            .then((json: any) => {
              if (json.detail) {
                logErrorService(json.detail, json.detail);
                dispatch(
                  addFlashMessages([{ msg: json.detail, defaultMessage: json.detail }], {
                    context: `${prefixEditTabMsg}${tabID}`,
                    location: 'simulation-alphas',
                  })
                );

                return dispatch(responseRecordsSetChartSimulationFailure(idParent, childId));
              } else {
                webSimApiErrorFlashMessages(dispatch, `${prefixEditTabMsg}${tabID}`, 'simulation-alphas');

                logErrorService(localeData.en['api.error.brain.experiencing'], localeData.en['api.error.brain.experiencing']);

                return dispatch(responseRecordsSetChartSimulationFailure(idParent, childId));
              }
            })
            .catch((error: any) => {
              webSimApiErrorFlashMessages(dispatch, `${prefixEditTabMsg}${tabID}`, 'simulation-alphas');

              logErrorService(error, error);

              return dispatch(responseRecordsSetChartSimulationFailure(idParent, childId));
            });
        }
      })
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'RESPONSE_RECORDS_SET_CHART_SIMULATION_FAILURE')) {
          return json;
        } else if (json === undefined) {
          return dispatch(responseRecordsSetChartSimulationFailure(idParent, childId));
        } else {
          return dispatch(responseRecordsSetChartSimulationSuccess(idParent, childId, json));
        }
      })
      .catch(error => {
        let tabID = childId ? childId : idParent;
        tabID = tabID ? tabID : idParent;
        logErrorService(error, localeData.en['api.error.brain.experiencing']);
        webSimApiErrorFlashMessages(dispatch, `${prefixEditTabMsg}${tabID}`, 'simulation-alphas');

        return dispatch(responseRecordsSetChartSimulationFailure(idParent, childId));
      });
  };

export const getRecordsSetSummarySimulation =
  (alphaId: string, record: string, idParent: string, childId: string | null) =>
  async (dispatch: any): Promise<object> => {
    if (alphaId === undefined || record === undefined) {
      return dispatch(invalidRequest());
    }

    dispatch(requestRecordsSetSummarySimulation(idParent, childId));

    return fetch(`${ENDPOINTS_ALPHAS.alpha}/${alphaId}/recordsets/${record}`, {
      method: 'GET',
      credentials: 'include',
    })
      .then(async (response: any): Promise<object> => {
        if (response.ok) {
          const timeOut = response.headers.get('Retry-After');
          if (timeOut !== null) {
            setTimeout(() => dispatch(getRecordsSetSummarySimulation(alphaId, record, idParent, childId)), timeOut * 1000);

            return dispatch(retryAfterRequest());
          } else {
            return response.json();
          }
        } else if (response.status === 401) {
          logErrorService(response.statusText, response.status);

          await dispatch(sessionExpire());

          return dispatch(responseRecordsSetSummarySimulationFailure(idParent, childId));
        } else {
          let tabID = childId ? childId : idParent;
          tabID = tabID ? tabID : idParent;

          return response
            .json()
            .then((json: any) => {
              if (json.detail) {
                logErrorService(json.detail, json.detail);
                dispatch(
                  addFlashMessages([{ msg: json.detail, defaultMessage: json.detail }], {
                    context: `${prefixEditTabMsg}${tabID}`,
                    location: 'simulation-alphas',
                  })
                );

                return dispatch(responseRecordsSetSummarySimulationFailure(idParent, childId));
              } else {
                webSimApiErrorFlashMessages(dispatch, `${prefixEditTabMsg}${tabID}`, 'simulation-alphas');

                logErrorService(localeData.en['api.error.brain.experiencing'], localeData.en['api.error.brain.experiencing']);

                return dispatch(responseRecordsSetSummarySimulationFailure(idParent, childId));
              }
            })
            .catch((error: any) => {
              webSimApiErrorFlashMessages(dispatch, `${prefixEditTabMsg}${tabID}`, 'simulation-alphas');

              logErrorService(error, error);

              return dispatch(responseRecordsSetSummarySimulationFailure(idParent, childId));
            });
        }
      })
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'RESPONSE_RECORDS_SET_SUMMARY_SIMULATION_FAILURE')) {
          return json;
        } else if (json === undefined) {
          return dispatch(responseRecordsSetSummarySimulationFailure(idParent, childId));
        } else {
          return dispatch(responseRecordsSetSummarySimulationSuccess(idParent, childId, json));
        }
      })
      .catch(error => {
        let tabID = childId ? childId : idParent;
        tabID = tabID ? tabID : idParent;
        logErrorService(error, localeData.en['api.error.brain.experiencing']);
        webSimApiErrorFlashMessages(dispatch, `${prefixEditTabMsg}${tabID}`, 'simulation-alphas');

        return dispatch(responseRecordsSetSummarySimulationFailure(idParent, childId));
      });
  };

export const getRecordsSetSimulation =
  (alphaId: string, idParent: string, childId: string | null) =>
  async (dispatch: any): Promise<object> => {
    if (alphaId === undefined) {
      return dispatch(invalidRequest());
    }

    dispatch(requestRecordsSetSimulation(idParent, childId));

    return fetch(`${ENDPOINTS_ALPHAS.alpha}/${alphaId}/recordsets`, {
      method: 'GET',
      credentials: 'include',
      headers: new Headers({ Accept: `application/json;${ENDPOINT_VERSION}` }),
    })
      .then(async (response: any): Promise<object> => {
        if (response.ok) {
          const timeOut = response.headers.get('Retry-After');
          if (timeOut !== null) {
            setTimeout(() => dispatch(getRecordsSetSimulation(alphaId, idParent, childId)), timeOut * 1000);

            return dispatch(retryAfterRequest());
          } else {
            return response.json();
          }
        } else if (response.status === 401) {
          logErrorService(response.statusText, response.status);

          await dispatch(sessionExpire());

          return dispatch(responseRecordsSetSimulationFailure(idParent, childId));
        } else {
          const tabID = childId ? childId : idParent;

          webSimApiErrorFlashMessages(dispatch, `${prefixEditTabMsg}${tabID}`, 'simulation-alphas');
          logErrorService(localeData.en['api.error.brain.experiencing'], localeData.en['api.error.brain.experiencing']);

          return dispatch(responseRecordsSetSimulationFailure(idParent, childId));
        }
      })
      .then(async (json: any): Promise<object> => {
        if (json === undefined) {
          return dispatch(responseRecordsSetSimulationFailure(idParent, childId));
        } else if (isAction(json, 'RESPONSE_RECORDS_SET_SIMULATION_FAILURE')) {
          return json;
        } else if (json.detail) {
          const tabID = childId ? childId : idParent;
          dispatch(
            addFlashMessages([{ msg: json.detail, defaultMessage: json.detail }], {
              context: `${prefixEditTabMsg}${tabID}`,
              location: 'simulation-alphas',
            })
          );
          logErrorService(json.detail, json.detail);

          return dispatch(responseRecordsSetSimulationFailure(idParent, childId));
        } else {
          return dispatch(responseRecordsSetSimulationSuccess(idParent, childId, json));
        }
      })
      .catch(() => {
        const tabID = childId ? childId : idParent;

        webSimApiErrorFlashMessages(dispatch, `${prefixEditTabMsg}${tabID}`, 'simulation-alphas');

        logErrorService(localeData.en['api.error.brain.experiencing'], localeData.en['api.error.brain.experiencing']);

        return dispatch(responseRecordsSetSimulationFailure(idParent, childId));
      });
  };
const errorMessagesStatusCode: any = [401, 410, 412, 429, 503];

export const getSimulationCorrelationsProd =
  (alphaId: string, idParent: string, childId: string | null) =>
  async (dispatch: any): Promise<object> => {
    if (alphaId === undefined) {
      return dispatch(invalidRequest());
    }

    dispatch(requestSimulationCorrelationProd(idParent, childId));

    return fetch(`${ENDPOINTS_ALPHAS.alpha}/${alphaId}/correlations/prod`, {
      method: 'GET',
      credentials: 'include',
      headers: new Headers({ Accept: `application/json;${ENDPOINT_VERSION}` }),
    })
      .then(async (response: any): Promise<object> => {
        if (response.ok) {
          const timeOut = response.headers.get('Retry-After');
          if (timeOut !== null) {
            setTimeout(() => dispatch(getSimulationCorrelationsProd(alphaId, idParent, childId)), timeOut * 1000);

            return dispatch(retryAfterRequest());
          } else {
            return response.json();
          }
        } else if (errorMessagesStatusCode.includes(response.status)) {
          return specialCasesCorrelation({
            response,
            dispatch,
            context: `${prefixEditTabMsg}${childId ? childId : idParent}`,
            dispatchFailure: () => dispatch(responseSimulationCorrelationProdFailure(idParent, childId, undefined, response.status)),
          });
        } else {
          dispatch(removeFlashMessagesContexts(['simulation-alphas'], [`${prefixEditTabMsg}${childId ? childId : idParent}`]));

          webSimApiErrorFlashMessages(dispatch, `${prefixEditTabMsg}${childId ? childId : idParent}`, 'simulation-alphas');

          return dispatch(responseSimulationCorrelationProdFailure(idParent, childId));
        }
      })
      .then(async (json: any): Promise<object> => {
        if (json === undefined) {
          return dispatch(invalidRequest());
        } else if (isAction(json, 'RESPONSE_SIMULATION_CORRELATION_PROD_FAILURE')) {
          return json;
        }
        const tabID = childId ? childId : idParent;
        if (json.detail) {
          logErrorService(json.detail, json.detail);
          dispatch(
            addFlashMessages([{ msg: json.detail, defaultMessage: json.detail }], {
              context: `${prefixEditTabMsg}${tabID}`,
              location: 'simulation-alphas',
            })
          );

          return dispatch(responseSimulationCorrelationProdFailure(idParent, childId));
        } else {
          if (t.exact(CorrelationProd).decode(json).isRight()) {
            return dispatch(responseSimulationCorrelationProdSuccess(idParent, childId, json));
          } else {
            webSimApiErrorFlashMessages(dispatch, `${prefixEditTabMsg}${tabID}`, 'simulation-alphas');
            // It will be reported to sentry the io-ts validation errors
            ioTsValidationError(json, CorrelationProd);

            return dispatch(responseSimulationCorrelationProdFailure(idParent, childId));
          }
        }
      })
      .catch(() => {
        dispatch(removeFlashMessagesContexts(['simulation-alphas'], [`${prefixEditTabMsg}${childId ? childId : idParent}`]));
        webSimApiErrorFlashMessages(dispatch, `${prefixEditTabMsg}${childId ? childId : idParent}`, 'simulation-alphas');

        logErrorService(localeData.en['api.error.brain.experiencing'], localeData.en['api.error.brain.experiencing']);

        return dispatch(responseSimulationCorrelationProdFailure(idParent, childId));
      });
  };

export const getSimulationCorrelationsPowerPool = (alphaId: string, idParent: string, childId: string | null, isConsultant: boolean) =>
  getSimulationCorrelationsResult({
    alphaId,
    idParent,
    childId,
    isConsultant,
    request: requestSimulationCorrelationPowerPool,
    responseFailure: responseSimulationCorrelationFailurePowerPool,
    responseSuccess: responseSimulationCorrelationSuccessPowerPool,
    failureType: 'RESPONSE_SIMULATION_CORRELATION_FAILURE_POWER_POOL',
    apiPath: 'power-pool',
  });

export const getSimulationCorrelations = (alphaId: string, idParent: string, childId: string | null, isConsultant: boolean) =>
  getSimulationCorrelationsResult({
    alphaId,
    idParent,
    childId,
    isConsultant,
    request: requestSimulationCorrelation,
    responseFailure: responseSimulationCorrelationFailure,
    responseSuccess: responseSimulationCorrelationSuccess,
    failureType: 'RESPONSE_SIMULATION_CORRELATION_FAILURE',
    apiPath: 'self',
  });

type SimulationCorrelationsResultInput = {
  alphaId: string;
  idParent: string;
  childId: string | null;
  isConsultant: boolean;
  request: (...args: any[]) => Action<any>;
  responseFailure: (...args: any[]) => Action<any>;
  responseSuccess: (...args: any[]) => Action<any>;
  failureType: string;
  apiPath: string;
};

const getSimulationCorrelationsResult =
  ({
    alphaId,
    idParent,
    childId,
    isConsultant,
    request,
    responseFailure,
    responseSuccess,
    failureType,
    apiPath,
  }: SimulationCorrelationsResultInput) =>
  async (dispatch: any): Promise<object> => {
    if (alphaId === undefined) {
      return dispatch(invalidRequest());
    }

    dispatch(request(idParent, childId));

    return fetch(`${ENDPOINTS_ALPHAS.alpha}/${alphaId}/correlations/${apiPath}`, {
      method: 'GET',
      credentials: 'include',
      headers: new Headers({ Accept: `application/json;${ENDPOINT_VERSION}` }),
    })
      .then(async (response: any): Promise<object> => {
        if (response.ok) {
          const timeOut = response.headers.get('Retry-After');
          if (timeOut !== null) {
            setTimeout(
              () =>
                dispatch(
                  getSimulationCorrelationsResult({
                    alphaId,
                    idParent,
                    childId,
                    isConsultant,
                    request,
                    responseFailure,
                    responseSuccess,
                    failureType,
                    apiPath,
                  })
                ),
              timeOut * 1000
            );

            return dispatch(retryAfterRequest());
          } else {
            return response.json();
          }
        } else if (errorMessagesStatusCode.includes(response.status)) {
          return specialCasesCorrelation({
            response,
            dispatch,
            context: `${prefixEditTabMsg}${childId ? childId : idParent}`,
            dispatchFailure: () => dispatch(responseFailure(idParent, childId, undefined, response.status)),
          });
        } else {
          dispatch(removeFlashMessagesContexts(['simulation-alphas'], [`${prefixEditTabMsg}${childId ? childId : idParent}`]));
          webSimApiErrorFlashMessages(dispatch, `${prefixEditTabMsg}${childId ? childId : idParent}`, 'simulation-alphas');
          logErrorService(localeData.en['api.error.brain.experiencing'], localeData.en['api.error.brain.experiencing']);

          return dispatch(responseFailure(idParent, childId));
        }
      })
      .then(async (json: any): Promise<object> => {
        if (json === undefined) {
          return dispatch(retryAfterRequest());
        } else if (isAction(json, failureType)) {
          return json;
        }
        const tabID = childId ? childId : idParent;
        if (json.detail) {
          dispatch(
            addFlashMessages([{ msg: json.detail, defaultMessage: json.detail }], {
              context: `${prefixEditTabMsg}${tabID}`,
              location: 'simulation-alphas',
            })
          );
          logErrorService(json.detail, json.detail);

          return dispatch(responseFailure(idParent, childId));
        } else {
          if (CorrelationSelf.decode(json).isRight()) {
            return dispatch(responseSuccess(idParent, childId, json));
          } else {
            webSimApiErrorFlashMessages(dispatch, `${prefixEditTabMsg}${tabID}`, 'simulation-alphas');
            // It will be reported to sentry the io-ts validation errors
            ioTsValidationError(json, CorrelationSelf);

            return dispatch(responseFailure(idParent, childId));
          }
        }
      })
      .catch(() => {
        dispatch(removeFlashMessagesContexts(['simulation-alphas'], [`${prefixEditTabMsg}${childId ? childId : idParent}`]));
        webSimApiErrorFlashMessages(dispatch, `${prefixEditTabMsg}${childId ? childId : idParent}`, 'simulation-alphas');

        logErrorService(localeData.en['api.error.brain.experiencing'], localeData.en['api.error.brain.experiencing']);

        return dispatch(responseFailure(idParent, childId));
      });
  };

const getAlphaPropertiesFromAlpha = (json: any) => {
  const color = json.color;
  const name = json.name;
  const tags = json.tags;
  const category = json.category;

  return {
    color: color && color !== 'NONE' && color.trim().length !== 0 ? color : null,
    name: name !== '' ? name : null,
    tags: tags,
    category: category !== '' ? category : null,
    regular: json.regular ? { description: json.regular.description } : undefined,
    combo: json.combo ? { description: json.combo.description } : undefined,
    selection: json.selection ? { description: json.selection.description } : undefined,
  };
};

const updateAlphaPropertiesWhenSimulationSucceed = (
  dispatch: any,
  json: any,
  alphaId: string,
  alphaProperties: any,
  idParent: string,
  childId: string | null
) => {
  const currentAlphaProperties = getAlphaPropertiesFromAlpha(json);

  // We (API) may update alpha properties after submission,
  // and users likely have already finished updating properties before the alpha submission.
  // In case the alpha was just submitted, update the properties to match the alpha.
  const isAlphaJustSubmitted = !!json.dateSubmitted;
  if (isAlphaJustSubmitted) {
    // Transform alpha properties to properties form data
    const alphaPropertiesData = mapValues(currentAlphaProperties, (value, key) => {
      if (key === 'tags') return Immutable.List(value);
      if (['regular', 'combo', 'selection'].includes(key)) return { description: value?.description ?? '', error: Immutable.List() };
      return value ?? '';
    });
    dispatch(resetProperties());
    dispatch(setAlphaPropertiesData(alphaPropertiesData));
    return dispatch(updateAlphaPropertiesSimulation(alphaId, currentAlphaProperties, idParent, childId));
  }

  if (alphaProperties && alphaProperties.get('hasUpdatedProperties')) {
    const color = alphaProperties.get('color');
    const name = alphaProperties.get('name');
    const tags = alphaProperties.get('tags');
    const category = alphaProperties.get('category');
    const payload: any = {
      color: color && color !== 'NONE' && color.trim().length !== 0 ? color : null,
      name: name !== '' ? name : null,
      tags: tags.toJS(),
      category: category !== '' ? category : null,
      regular: alphaProperties.getIn(['regular', 'description'])
        ? { description: alphaProperties.getIn(['regular', 'description']) }
        : undefined,
      combo: alphaProperties.getIn(['combo', 'description']) ? { description: alphaProperties.getIn(['combo', 'description']) } : undefined,
      selection: alphaProperties.getIn(['selection', 'description'])
        ? { description: alphaProperties.getIn(['selection', 'description']) }
        : undefined,
    };
    if (!Immutable.fromJS(currentAlphaProperties).equals(Immutable.fromJS(payload))) {
      return dispatch(updateAlphaPropertiesSimulation(alphaId, payload, idParent, childId));
    }
  }

  return null;
};

const getAlphaProperties = (state: any, idParent: string, childId: string | null) => {
  const simulationData = state.getIn(['simulation', 'data']);
  const tabIndex = simulationData.findIndex((item: any) => item.get('id') === idParent);
  if (tabIndex >= 0) {
    const children = simulationData.getIn([tabIndex, 'children']);
    const indexChild = children && childId && children.findIndex((item: any) => item.get('id') === childId);
    if (indexChild !== null && indexChild !== undefined && indexChild >= 0) {
      return simulationData.getIn([tabIndex, 'children', indexChild, 'alphaProperties']);
    } else if (!children) {
      return simulationData.getIn([tabIndex, 'alphaProperties']);
    }
  }

  return null;
};

export function getSimulationData(idParent: string, childId: string | null, alpha: string, isVisualization: boolean) {
  return async (dispatch: any, getState: any): Promise<object> => {
    if (alpha === undefined) {
      return dispatch(invalidRequest());
    }
    dispatch(requestDataSimulate(idParent, childId));

    return fetch(`${ENDPOINTS_ALPHAS.alpha}/${alpha}`, {
      method: 'GET',
      credentials: 'include',
      headers: new Headers({ Accept: `application/json;${ENDPOINT_VERSION}` }),
    })
      .then(async (response: any): Promise<object> => {
        const timeOut = response.headers.get('Retry-After');
        if (timeOut !== null) {
          setTimeout(() => dispatch(getSimulationData(idParent, childId, alpha, isVisualization)), timeOut * 1000);

          return dispatch(retryAfterRequest());
        } else if (response.status === 401) {
          logErrorService(response.statusText, response.status);

          await dispatch(sessionExpire());

          return dispatch(responseDataSimulateFailure(idParent, childId));
        } else {
          return response.json();
        }
      })
      .then(async (json: any): Promise<object> => {
        const tabID = childId ? childId : idParent;
        dispatch(removeFlashMessagesContextsErrors(['editor-code'], [`${prefixEditTabMsg}${tabID}`]));
        if (isAction(json, 'RESPONSE_DATA_SIMULATE_FAILURE')) {
          return json;
        } else if (json.detail) {
          dispatch(
            addFlashMessages([{ msg: json.detail, defaultMessage: json.detail }], {
              context: `${prefixEditTabMsg}${tabID}`,
              location: 'editor-code',
            })
          );
          logErrorService(json.detail, json.detail);

          return dispatch(responseDataSimulateFailure(idParent, childId));
        } else if (!t.exact(AlphasDataResults).decode(json).isRight()) {
          dispatch(
            addFlashMessages(
              [{ msg: 'api.error.json-parameters-invalid', defaultMessage: localeData.en['api.error.json-parameters-invalid'] }],
              { context: `${prefixEditTabMsg}${tabID}`, location: 'editor-code' }
            )
          );
          // It will be reported to sentry the io-ts validation errors
          ioTsValidationError(json, AlphasDataResults);

          return dispatch(responseDataSimulateFailure(idParent, childId));
        } else {
          return dispatch(getRecordsSetSimulation(alpha, idParent, childId)).then(() => {
            dispatch(responseDataSimulateSuccess(idParent, childId, json));
            const state = getState();
            const alphaProperties = getAlphaProperties(state, idParent, childId);

            return updateAlphaPropertiesWhenSimulationSucceed(dispatch, json, alpha, alphaProperties, idParent, childId);
          });
        }
      })
      .catch(e => {
        dispatch(removeFlashMessagesContextsErrors(['editor-code'], [`${prefixEditTabMsg}${childId ? childId : idParent}`]));
        webSimApiErrorFlashMessages(dispatch, `${prefixEditTabMsg}${childId ? childId : idParent}`, 'simulation-alphas');

        logErrorService(e, e);

        return dispatch(responseDataSimulateFailure(idParent, childId));
      });
  };
}

export function simulationCheck(alphaId: string, idParent: string, childId: string | null) {
  return async (dispatch: any): Promise<object> => {
    if (alphaId === undefined) {
      return dispatch(invalidRequest());
    }

    dispatch(requestSimulationCheck(idParent, childId));

    return fetch(`${ENDPOINTS_ALPHAS.alpha}/${alphaId}/check`, {
      method: 'GET',
      credentials: 'include',
      headers: new Headers({ Accept: `application/json;${ENDPOINT_VERSION}` }),
    })
      .then(async (response: any): Promise<object> => {
        const timeOut = response.headers.get('Retry-After');
        if (timeOut !== null) {
          setTimeout(() => dispatch(simulationCheck(alphaId, idParent, childId)), parseFloat(timeOut) * 1000);

          return dispatch(retryAfterRequest());
        } else if (response.status === 429) {
          dispatch(
            addFlashMessages(
              [
                {
                  msg: 'alphas.check.too.many.requests',
                  defaultMessage: localeData.en['alphas.check.too.many.requests'],
                },
              ],
              {
                context: `${prefixEditTabMsg}${childId ? childId : idParent}`,
                location: 'simulation-alphas',
              }
            )
          );

          return dispatch(responseSimulationCheckFailure(idParent, childId));
        } else if (response.status === 401) {
          logErrorService(response.statusText, response.status);

          dispatch(sessionExpire());

          return dispatch(responseSimulationCheckFailure(idParent, childId));
        } else {
          return response.json();
        }
      })
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'RESPONSE_SIMULATION_CHECK_FAILURE')) {
          return json;
        } else if (t.exact(CheckSubmissionValidation).decode(json).isRight()) {
          if (json.is.selfCorrelation) {
            dispatch(responseSimulationCorrelationSuccess(idParent, childId, json.is.selfCorrelation));
          }
          const failedChecks = json.is.checks.filter((metric: any) => metric.result === 'FAIL');
          if (failedChecks.length === 0) {
            return dispatch(responseSimulationCheck(idParent, childId, json.is.checks));
          }
          dispatch(
            addFlashMessages(
              [
                {
                  msg: 'dataAction.failedChecks',
                  defaultMessage: localeData.en['dataAction.failedChecks'],
                  intlValues: { count: failedChecks.length },
                },
              ],
              {
                context: `${prefixEditTabMsg}${childId ? childId : idParent}`,
                location: 'simulation-alphas',
              }
            )
          );

          return dispatch(responseSimulationCheck(idParent, childId, json.is.checks));
        } else {
          // It will be reported to sentry the io-ts validation errors
          ioTsValidationError(json, CheckSubmissionValidation);

          return dispatch(responseSimulationCheckFailure(idParent, childId));
        }
      })
      .catch((err: any) => {
        logErrorService(err, err);

        return dispatch(responseSimulationCheckFailure(idParent, childId));
      });
  };
}

const submitAlphaCheckErrorSimulation = async (
  result: any,
  dispatch: any,
  alpha: string,
  idParent: string,
  childId: string | null,
  isVisualization: any
): Promise<object> => {
  if (result.status === 'success') {
    dispatch(
      addFlashMessages([{ msg: 'dataAction.alphaSucess', defaultMessage: localeData.en['dataAction.alphaSucess'] }], {
        context: `${prefixEditTabMsg}${childId ? childId : idParent}`,
        location: 'alpha-details',
      })
    );
    const simDiv: any = document.querySelectorAll('.editor-simulation ')[0];
    const simMessageDiv: any = document.querySelectorAll('.flash--tags-success')[0];
    if (simDiv && simMessageDiv) {
      animateScrollTo(simMessageDiv.offsetTop, { element: simDiv });
    }
    dispatch(responseSimulationCheck(idParent, childId, result.json.is && result.json.is.checks));
    dispatch(getSimulationData(idParent, childId, alpha, isVisualization));

    return dispatch(responseSubmissionSimulationSuccess(idParent, childId));
  } else if (result.status === 'failed') {
    if (result.json.is.selfCorrelation) {
      dispatch(responseSimulationCorrelationSuccess(idParent, childId, result.json.is && result.json.is.selfCorrelation));
    }
    const failedChecks = result.json.is && result.json.is.checks.filter((metric: any) => metric.result === 'FAIL');
    if (failedChecks.length > 0) {
      dispatch(
        addFlashMessages(
          [
            {
              msg: 'dataAction.failedChecks',
              defaultMessage: localeData.en['dataAction.failedChecks'],
              intlValues: { count: failedChecks.length },
            },
          ],
          {
            location: 'simulation-alphas',
            context: `${prefixEditTabMsg}${childId ? childId : idParent}`,
          }
        )
      );
    }
    const simDiv: any = document.querySelectorAll('.editor-simulation ')[0];
    const simMessageDiv: any = document.querySelectorAll('.flash--check-alphas')[0];
    if (simDiv && simMessageDiv) {
      animateScrollTo(simMessageDiv.offsetTop, { element: simDiv });
    }
    dispatch(responseSimulationCheck(idParent, childId, result.json.is.checks));

    return dispatch(responseSubmissionSimulationFailure(idParent, childId));
  } else {
    return dispatch(responseSubmissionSimulationFailure(idParent, childId));
  }
};

export function submitAlphaSimulation(
  alpha: string,
  idParent: string,
  childId: string | null,
  isVisualization: boolean,
  method: string = 'POST'
) {
  return async (dispatch: any): Promise<object> => {
    if (alpha === undefined) {
      return dispatch(invalidRequest());
    }

    dispatch(requestSubmissionSimulation(idParent, childId));

    return fetch(`${ENDPOINTS_ALPHAS.alpha}/${alpha}/submit`, {
      method: method,
      credentials: 'include',
      headers: new Headers({ Accept: `application/json;${ENDPOINT_VERSION}` }),
    })
      .then(async (response: any): Promise<object> => {
        if (response.ok) {
          const timeOut = response.headers.get('Retry-After');
          if (timeOut !== null) {
            setTimeout(() => dispatch(submitAlphaSimulation(alpha, idParent, childId, isVisualization, (method = 'GET'))), timeOut * 1000);

            return dispatch(retryAfterRequest());
          } else {
            return response
              .json()
              .then(
                async (json: any): Promise<object> =>
                  submitAlphaCheckErrorSimulation({ status: 'success', json }, dispatch, alpha, idParent, childId, isVisualization)
              );
          }
        } else if (response.status === 401) {
          logErrorService(response.statusText, response.status);

          dispatch(sessionExpire());

          return dispatch(responseSubmissionSimulationFailure(idParent, childId));
        } else if (response.status === 429) {
          dispatch(
            addFlashMessages(
              [
                {
                  msg: 'alphas.submit.too.many.requests',
                  defaultMessage: localeData.en['alphas.submit.too.many.requests'],
                },
              ],
              {
                context: `${prefixEditTabMsg}${childId ? childId : idParent}`,
                location: 'simulation-alphas',
                links: [
                  {
                    urls: [`${FAQ_URL}/articles/18423410021783--BRAIN-TIPS-Troubleshooting-common-error-messages`],
                    keys: ['alphas.submit.too.many.requests.link'],
                    defaultMsgkeys: [localeData.en['alphas.submit.too.many.requests.link']],
                  },
                ],
              }
            )
          );

          return dispatch(responseSubmissionSimulationFailure(idParent, childId));
        } else if (response.status === 500) {
          logErrorService(response.statusText, response.status);

          return dispatch(responseSubmissionSimulationFailure(idParent, childId));
        } else {
          return response
            .json()
            .then(
              async (json: any): Promise<object> =>
                submitAlphaCheckErrorSimulation({ status: 'failed', json: json }, dispatch, alpha, idParent, childId, isVisualization)
            );
        }
      })
      .catch((err: any) => {
        logErrorService(err, err);

        return dispatch(responseSubmissionSimulationFailure(idParent, childId));
      });
  };
}

export function updateAlphaPropertiesSimulation(alphaId: string, data: any, idParent: string, childIndex: any) {
  return async (dispatch: any, getState: any): Promise<object> => {
    const state = getState();
    const tabIndex = state.getIn(['editor', 'tabs']).findIndex((item: any) => item.get('id') === idParent);
    const childId = childIndex >= 0 ? state.getIn(['editor', 'tabs', tabIndex, 'childTabs', childIndex, 'id']) : childIndex;
    if (alphaId === undefined) {
      return dispatch(invalidRequest());
    }
    let payload = data;
    if (data === undefined) {
      const color = state.getIn(['alphaProperties', 'color']);
      const name = state.getIn(['alphaProperties', 'name']);
      const tags = state.getIn(['alphaProperties', 'tags']);
      const category = state.getIn(['alphaProperties', 'category']);
      const descriptionRegular = state.getIn(['alphaProperties', 'regular', 'description']);
      const descriptionSelection = state.getIn(['alphaProperties', 'selection', 'description']);
      const descriptionCombo = state.getIn(['alphaProperties', 'combo', 'description']);
      payload = {
        color: color !== 'NONE' && color.trim().length !== 0 ? color : null,
        name: name !== '' ? name : null,
        tags: tags.toJS(),
        category: category !== '' ? category : null,
        regular: descriptionRegular !== '' ? { description: descriptionRegular } : undefined,
        combo: descriptionCombo !== '' ? { description: descriptionCombo } : undefined,
        selection: descriptionSelection !== '' ? { description: descriptionSelection } : undefined,
      };
    }
    dispatch(requestUpdateAlphaPropertiesSimulation(idParent, childId));

    return fetch(`${ENDPOINTS_ALPHAS.alpha}/${alphaId}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: new Headers({
        'Content-Type': 'application/json',
        Accept: `application/json;${ENDPOINT_VERSION}`,
      }),
      body: JSON.stringify(payload),
    })
      .then(async (response: any): Promise<object> => {
        if (response.ok) {
          return response
            .json()
            .then(async (json: any): Promise<object> => {
              if (json.detail) {
                dispatch(
                  addFlashMessages([{ msg: json.detail, defaultMessage: localeData.en[json.detail] }], {
                    location: 'simulation-alphas',
                    context: `${prefixEditTabMsg}${childId ? childId : idParent}`,
                  })
                );
                logErrorService(json.detail, json.detail);

                return dispatch(responseUpdateAlphaPropertiesSimulationFailure(idParent, childId));
              } else {
                return dispatch(responseUpdateAlphaPropertiesSimulation(idParent, childId, json));
              }
            })
            .catch((err: any) => {
              dispatch(
                addFlashMessages([{ msg: 'api.error.simulation-later', defaultMessage: localeData.en['api.error.simulation-later'] }], {
                  location: 'simulation-alphas',
                  context: `${prefixEditTabMsg}${childId ? childId : idParent}`,
                })
              );
              logErrorService(err, err);

              return dispatch(responseUpdateAlphaPropertiesSimulationFailure(idParent, childId));
            });
        } else if (response.status === 401) {
          logErrorService(response.statusText, response.status);

          dispatch(sessionExpire());

          return dispatch(responseUpdateAlphaPropertiesSimulationFailure(idParent, childId));
        } else {
          return response
            .json()
            .then(async (json: any): Promise<object> => {
              const errorMessages = concatStringInArrayOfOBjects(json);
              dispatch(
                addFlashMessages(errorMessages, {
                  context: `${prefixEditTabMsg}${childId ? childId : idParent}`,
                  location: 'simulation-alphas',
                })
              );
              logErrorService(json, json);

              return dispatch(responseUpdateAlphaPropertiesSimulationFailure(idParent, childId));
            })
            .catch((err: any) => {
              logErrorService(err, err);
              webSimApiErrorFlashMessages(dispatch, `${prefixEditTabMsg}${childId ? childId : idParent}`, 'simulation-alphas');

              return dispatch(responseUpdateAlphaPropertiesSimulationFailure(idParent, childId));
            });
        }
      })
      .catch((err: any) => {
        logErrorService(err, err);
        webSimApiErrorFlashMessages(dispatch, `${prefixEditTabMsg}${childId ? childId : idParent}`, 'simulation-alphas');

        return dispatch(responseUpdateAlphaPropertiesSimulationFailure(idParent, childId));
      });
  };
}

export const getSimulationPerformanceComparison =
  (alphaId: string, idParent: string, childId: string | null) =>
  async (dispatch: any, getState: any): Promise<object> => {
    const state = getState();

    const teamId = state.getIn(['team', 'activeTeam', 'activeTeam', 'id']);
    const tab = state.getIn(['simulation', 'data']).find((tabData: any) => tabData.get('id') === idParent);
    if (alphaId === undefined || !tab) {
      return dispatch(invalidRequest());
    }
    dispatch(requestSimulationAlphasPerformanceComparison(idParent, childId));

    const scoringCompetition = getScoringCompetition(state, tab, alphaId);
    return globalFetch(
      getPerformanceEndpoint({ alphaId: alphaId, teamId: teamId, scoringCompetition: scoringCompetition }),
      {
        method: 'GET',
        credentials: 'include',
      },
      dispatch,
      () => dispatch(responseSimulationAlphasPerformanceComparisonFailure(idParent, childId)),
      {
        callbackAction: () => dispatch(getSimulationPerformanceComparison(alphaId, idParent, childId)),
        retryEmpty: true,
      }
    )
      .then((json: any) => {
        if (json && json.type === 'RESPONSE_SIMULATION_ALPHAS_PERFORMANCE_COMPARISON_FAILURE') {
          logErrorService(localeData.en['api.error.brain.experiencing'], localeData.en['api.error.brain.experiencing']);
          webSimApiErrorFlashMessages(dispatch, `${prefixEditTabMsg}${childId ? childId : idParent}`, 'simulation-alphas');

          return json;
        } else if (isAction(json, '')) {
          return json;
        } else {
          return dispatch(responseSimulationAlphasPerformanceComparisonSuccess(idParent, childId, json, moment().utc().format()));
        }
      })
      .catch((error: any) => {
        logErrorService(error, error);
        dispatch(
          addFlashMessages([{ msg: error, defaultMessage: localeData.en[error] }], {
            location: 'simulation-alphas',
            context: `${prefixEditTabMsg}${childId ? childId : idParent}`,
          })
        );

        return dispatch(responseSimulationAlphasPerformanceComparisonFailure(idParent, childId));
      });
  };

export function getAlphasFromAlpha(idParent: string, alpha: string) {
  return async (dispatch: any): Promise<object> => {
    if (alpha === undefined) {
      return dispatch(invalidRequest());
    }
    dispatch(requestAlphasFromAlphaSimulate(idParent));

    return fetch(`${ENDPOINTS_ALPHAS.alpha}/${alpha}/alphas`, {
      method: 'GET',
      credentials: 'include',
      headers: new Headers({ Accept: `application/json;${ENDPOINT_VERSION}` }),
    })
      .then(async (response: any): Promise<object> => {
        const timeOut = response.headers.get('Retry-After');
        if (timeOut !== null) {
          setTimeout(() => dispatch(getAlphasFromAlpha(idParent, alpha)), timeOut * 1000);

          return dispatch(retryAfterRequest());
        } else if (response.status === 401) {
          logErrorService(response.statusText, response.status);

          await dispatch(sessionExpire());

          return dispatch(responseAlphasFromAlphaSimulateFailure(idParent));
        } else {
          return response.json();
        }
      })
      .then(async (json: any): Promise<object> => {
        const tabID = idParent;
        dispatch(removeFlashMessagesContextsErrors(['editor-code'], [`${prefixEditTabMsg}${tabID}`]));
        if (isAction(json, 'RESPONSE_DATA_SIMULATE_FAILURE')) {
          return json;
        } else if (json.detail) {
          dispatch(
            addFlashMessages([{ msg: json.detail, defaultMessage: json.detail }], {
              context: `${prefixEditTabMsg}${tabID}`,
              location: 'editor-code',
            })
          );
          logErrorService(json.detail, json.detail);

          return dispatch(responseAlphasFromAlphaSimulateFailure(idParent));
        } else if (!t.exact(AlphasListingData).decode(json).isRight()) {
          dispatch(
            addFlashMessages(
              [{ msg: 'api.error.json-parameters-invalid', defaultMessage: localeData.en['api.error.json-parameters-invalid'] }],
              { context: `${prefixEditTabMsg}${tabID}`, location: 'editor-code' }
            )
          );
          // It will be reported to sentry the io-ts validation errors
          ioTsValidationError(json, AlphasListingData);

          return dispatch(responseAlphasFromAlphaSimulateFailure(idParent));
        } else {
          return dispatch(responseAlphasFromAlphaSimulateSuccess(idParent, json));
        }
      })
      .catch(e => {
        dispatch(removeFlashMessagesContextsErrors(['editor-code'], [`${prefixEditTabMsg}${idParent}`]));
        webSimApiErrorFlashMessages(dispatch, `${prefixEditTabMsg}${idParent}`, 'simulation-alphas');

        logErrorService(e, e);

        return dispatch(responseAlphasFromAlphaSimulateFailure(idParent));
      });
  };
}

export const getSelectionCode =
  (
    id: string,
    selection: string,
    delay: string,
    region: string,
    instrumentType: string,
    selectionLimit: string,
    selectionHandling: string
  ) =>
  async (dispatch: any): Promise<object> => {
    const tabID = id;

    const location = 'editor-code';
    const context = `${prefixEditTabMsg}${tabID}`;

    const updateFlashMessage = (msg: string) => {
      dispatch(removeFlashContexts([location], [context]));
      dispatch(addFlashMessages([{ msg, defaultMessage: localeData.en[msg] }], { location, context }));
    };

    dispatch(removeFlashContexts([location], [context]));
    dispatch(requestSelectionCode(id, selection, delay, region, instrumentType, selectionLimit, selectionHandling));

    return globalFetch(
      `${ENDPOINTS_SIMULATIONS.selection}?settings.instrumentType=${instrumentType}&settings.region=${region}&settings.delay=${delay}&selection=${selection}&limit=10&selectionLimit=${selectionLimit}&selectionHandling=${selectionHandling}`,
      {
        method: 'GET',
        credentials: 'include',
      },
      dispatch,
      () => dispatch(responseSelectionCodeFailure(id)),
      {
        callbackStatus429: () => updateFlashMessage('alphas.selection.too.many.requests'),
        callbackStatus400: async (response: any) =>
          response
            .json()
            .then((json: any) => {
              if (json && json.location) {
                dispatch(setTabCodeError(json.location, id, null));
                const msg = json.message ? json.message : 'api.error.simulation-alpha';
                updateFlashMessage(msg);
              } else if (json) {
                dispatch(addFlashMessages(json, { intl: false }));
              }

              return dispatch(responseSelectionCodeFailure(id));
            })
            .catch((err: any) => {
              logErrorService(err, err);
              webSimApiErrorFlashMessages(dispatch);

              return dispatch(responseSelectionCodeFailure(id));
            }),
      }
    )
      .then(json => {
        if (isAction(json, 'RESPONSE_SELECTION_CODE_FAILURE')) {
          return json;
        } else if (t.exact(AlphasListingData).decode(json).isRight()) {
          return dispatch(responseSelectionCodeSuccess(id, json));
        } else if (json && json.detail) {
          dispatch(addFlashMessages([{ msg: json.detail, defaultMessage: localeData.en[json.detail] }]));
          logErrorService(json.detail, json.detail);

          return dispatch(responseSelectionCodeFailure(id));
        } else {
          // It will be reported to sentry the io-ts validation errors
          ioTsValidationError(json, AlphasListingData);
          dispatch(
            addFlashMessages([{ msg: 'editorSettings.error.message', defaultMessage: localeData.en['editorSettings.error.message'] }], {
              context: `${prefixEditTabMsg}${tabID}`,
              location: 'editor-code',
            })
          );

          return dispatch(responseSelectionCodeFailure(id));
        }
      })
      .catch((err: any) => {
        logErrorService(err, err);
        webSimApiErrorFlashMessages(dispatch);

        return dispatch(responseSelectionCodeFailure(id));
      });
  };
